import { useEffect, useState, type FormEvent } from "react";
import { Footer } from "../shell/Footer";
import { sb } from "../lib/supabase";
import { CONFIG } from "../lib/config";
import { useSession } from "./session";
import { passwordProblem } from "../account/password";

const SSO: Record<string, string> = { google: "Google", azure: "Microsoft" };

// The Altekore sign-in: the same accounts as the other Altekore products in this region.
export function SignIn() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ text: string; kind: "err" | "ok" } | null>(null);
  const [providers, setProviders] = useState<string[]>([]);
  const [mode, setMode] = useState<"in" | "up">(new URLSearchParams(location.search).has("signup") ? "up" : "in");
  const [name, setName] = useState(""); const [agree, setAgree] = useState(false);

  useEffect(() => {
    fetch(`${CONFIG.supabaseUrl}/auth/v1/settings`, { headers: { apikey: CONFIG.supabaseKey } })
      .then((r) => r.json())
      .then((s) => setProviders(Object.keys(SSO).filter((p) => s?.external?.[p])))
      .catch(() => setProviders([]));
  }, []);

  const signUp = async () => {
    const p = passwordProblem(password); if (p) { setMsg({ text: p, kind: "err" }); return; }
    if (!agree) { setMsg({ text: "Agree to the terms and privacy notice to go on.", kind: "err" }); return; }
    setBusy(true); setMsg(null);
    const { data, error } = await sb.auth.signUp({ email: email.trim(), password, options: { data: { full_name: name.trim() }, emailRedirectTo: `${location.origin}/` } });
    setBusy(false);
    if (error) setMsg({ text: error.message, kind: "err" });
    else if (!data.session) setMsg({ text: "Check your email for a link to confirm the address, then sign in. Your 14-day trial starts with your first company.", kind: "ok" });
  };
  const signIn = async (e: FormEvent) => {
    e.preventDefault();
    if (mode === "up") { await signUp(); return; }
    setBusy(true); setMsg(null);
    const { error } = await sb.auth.signInWithPassword({ email: email.trim(), password });
    setBusy(false);
    if (error) setMsg({ text: error.message, kind: "err" });
  };

  const forgot = async () => {
    if (!email.trim()) { setMsg({ text: "Enter your email first.", kind: "err" }); return; }
    const { error } = await sb.auth.resetPasswordForEmail(email.trim(), { redirectTo: `${location.origin}/` });
    setMsg(error ? { text: error.message, kind: "err" } : { text: "If that email has an Altekore account, a reset link is on its way.", kind: "ok" });
  };

  const sso = async (p: string) => {
    const { error } = await sb.auth.signInWithOAuth({
      provider: p as "google" | "azure",
      options: { redirectTo: `${location.origin}/`, scopes: p === "azure" ? "openid email profile" : undefined, queryParams: { prompt: "select_account" } },
    });
    if (error) setMsg({ text: `Couldn't open ${SSO[p]} sign-in: ${error.message}`, kind: "err" });
  };

  return (
    <main className="auth split">
      <aside className="auth-side">
        <div className="brand"><img className="mark" src="/mark.svg" alt="" width={28} height={28} /><span>Altekore Books</span></div>
        <div className="auth-pitch">
          <h2>Accounts, GST and TDS for Indian businesses, kept in one ledger.</h2>
          <ul>
            <li>Invoices, bills and stock that post themselves</li>
            <li>GSTR-1, 3B, e-invoices and e-way bills from the same entries</li>
            <li>TDS on the Income-tax Act, 2025</li>
            <li>Your data stays in India</li>
          </ul>
        </div>
      </aside>
      <div className="auth-main">
      <form className="card auth-card" onSubmit={signIn}>
        <h1>{mode === "up" ? "Start your free trial" : "Sign in"}</h1>
        <p className="muted">{mode === "up" ? "14 days, every feature, no card needed." : "Use your Altekore account."}</p>
        {mode === "up" && <label>Your name<input autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} required /></label>}
        <label>Email<input type="email" autoComplete="username" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} required /></label>
        <label>Password<input type="password" autoComplete={mode === "up" ? "new-password" : "current-password"} value={password} onChange={(e) => setPassword(e.target.value)} required /></label>
        {mode === "up" && <><p className="muted small">At least 10 characters, with a letter and a digit.</p>
          <label className="check"><input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} /> I agree to the <a href={CONFIG.termsUrl} target="_blank" rel="noreferrer">terms of service</a> and
            the <a href={CONFIG.privacyUrl} target="_blank" rel="noreferrer">privacy notice</a> (data kept in India, under the DPDP Act, 2023)</label></>}
        <button className="primary" disabled={busy}>{busy ? (mode === "up" ? "Creating…" : "Signing in…") : mode === "up" ? "Create my account" : "Sign in"}</button>
        {mode === "in" ? <><button type="button" className="link" onClick={forgot}>Forgot password?</button>
          <p className="small">New to Altekore Books? <button type="button" className="link" onClick={() => { setMode("up"); setMsg(null); }}>Start a free trial</button></p></>
          : <p className="small">Have an account? <button type="button" className="link" onClick={() => { setMode("in"); setMsg(null); }}>Sign in</button></p>}
        {providers.length > 0 && (
          <div className="sso">
            <span className="muted">or</span>
            {providers.map((p) => <button type="button" key={p} onClick={() => sso(p)}>Continue with {SSO[p]}</button>)}
          </div>
        )}
        {msg && <p className={`msg ${msg.kind}`} role="status">{msg.text}</p>}
      </form>
      <Footer kind="public" />
      </div>
    </main>
  );
}

// Two-step verification, when the account has it on
export function MfaChallenge() {
  const { refresh } = useSession();
  const [code, setCode] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const verify = async (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    const { data, error } = await sb.auth.mfa.listFactors();
    const factor = data?.totp?.find((f) => f.status === "verified");
    if (error || !factor) { setErr(error?.message ?? "No authenticator is set up on this account."); return; }
    const r = await sb.auth.mfa.challengeAndVerify({ factorId: factor.id, code: code.trim() });
    if (r.error) setErr(r.error.message); else await refresh();
  };
  return (
    <main className="auth">
      <form className="card auth-card" onSubmit={verify}>
        <h1>Two-step verification</h1>
        <p className="muted">Enter the 6-digit code from your authenticator app.</p>
        <input inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} autoFocus value={code}
               onChange={(e) => setCode(e.target.value)} aria-label="Code" />
        <button className="primary">Verify</button>
        <button type="button" className="link" onClick={() => sb.auth.signOut()}>Sign out</button>
        {err && <p className="msg err" role="status">{err}</p>}
      </form>
    </main>
  );
}
